import { type MutableRefObject, useCallback } from 'react'
import { toast } from 'sonner'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  FREEZE_BLOCKED_BY_LOCK_MESSAGE,
  FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE,
  LOCK_BLOCKED_BY_FREEZE_MESSAGE,
} from '@/lib/editor/freeze-object-messages'
import { buildSortedIdsTxnId } from '../helpers/sorted-ids-txn-id'

/** The live shapes among `selectedShapeIds`, in shape-list order. */
function selectedShapesOf(mgr: ShapeManagerRef | null, selectedShapeIds: string[]): ShapeData[] {
  return (mgr?.getShapes() ?? []).filter((shape) => selectedShapeIds.includes(shape.id))
}

/** Ids of every live shape matching `predicate`, in shape-list order. */
function shapeIdsWhere(
  mgr: ShapeManagerRef | null,
  predicate: (shape: ShapeData) => boolean
): string[] {
  return (mgr?.getShapes() ?? []).filter(predicate).map((shape) => shape.id)
}

/**
 * Lock / unlock / freeze / unfreeze commands for the selection and for the whole spread, each a
 * single undo step keyed by the affected ids, plus the "geometry is blocked" pulse the refusals
 * share.
 */
export function useLockFreezeCommands({
  internalShapeManagerRef,
  activeIsolationGroupId,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  activeIsolationGroupId: string | null
}) {
  const lockSelectedObjects = useCallback(() => {
    if (activeIsolationGroupId) return

    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 0) return

    const shapes = selectedShapesOf(mgr, selectedShapeIds)
    const selectionHasAnyFrozen = shapes.some((s) => s.frozen === true)

    if (selectionHasAnyFrozen) {
      toast.error(LOCK_BLOCKED_BY_FREEZE_MESSAGE)
      return
    }

    mgr?.lockSelected({ historyTxnId: buildSortedIdsTxnId('lock', selectedShapeIds) })
  }, [activeIsolationGroupId, internalShapeManagerRef])

  const unlockSelectedObjects = useCallback(() => {
    if (activeIsolationGroupId) return

    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 0) return

    mgr?.unlockSelected({ historyTxnId: buildSortedIdsTxnId('unlock', selectedShapeIds) })
  }, [activeIsolationGroupId, internalShapeManagerRef])

  const freezeSelectedObjects = useCallback(() => {
    if (activeIsolationGroupId) {
      toast.error(FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE)
      return
    }

    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 0) return

    const shapes = selectedShapesOf(mgr, selectedShapeIds)
    const selectionHasAnyLocked = shapes.some((s) => s.locked === true)

    if (selectionHasAnyLocked) {
      toast.error(FREEZE_BLOCKED_BY_LOCK_MESSAGE)
      return
    }

    mgr?.freezeSelected({ historyTxnId: buildSortedIdsTxnId('freeze', selectedShapeIds) })
  }, [activeIsolationGroupId, internalShapeManagerRef])

  const unfreezeSelectedObjects = useCallback(() => {
    if (activeIsolationGroupId) {
      toast.error(FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE)
      return
    }

    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 0) return

    mgr?.unfreezeSelected({ historyTxnId: buildSortedIdsTxnId('unfreeze', selectedShapeIds) })
  }, [activeIsolationGroupId, internalShapeManagerRef])

  const toggleFreezeSelectedObjects = useCallback(() => {
    if (activeIsolationGroupId) {
      toast.error(FREEZE_UNAVAILABLE_IN_GROUP_MESSAGE)
      return
    }

    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 0) return

    const shapes = selectedShapesOf(mgr, selectedShapeIds)
    const allFrozen = shapes.length === 0 || shapes.every((shape) => shape.frozen === true)

    if (allFrozen) {
      unfreezeSelectedObjects()
    } else {
      freezeSelectedObjects()
    }
  }, [
    activeIsolationGroupId,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    internalShapeManagerRef,
  ])

  const unlockAllObjects = useCallback(() => {
    const mgr = internalShapeManagerRef.current
    const lockedShapeIds = shapeIdsWhere(mgr, (shape) => shape.locked === true)

    if (lockedShapeIds.length === 0) return

    mgr?.unlockAll({ historyTxnId: buildSortedIdsTxnId('unlock-all', lockedShapeIds) })
  }, [internalShapeManagerRef])

  const unfreezeAllObjects = useCallback(() => {
    const mgr = internalShapeManagerRef.current
    const frozenShapeIds = shapeIdsWhere(mgr, (shape) => shape.frozen === true)

    if (frozenShapeIds.length === 0) return

    mgr?.unfreezeAll({ historyTxnId: buildSortedIdsTxnId('unfreeze-all', frozenShapeIds) })
  }, [internalShapeManagerRef])

  // Shared by every refusal that pulses the selection chrome: Cut and Delete on a frozen
  // selection, and the node-edit / crop entry and drag guards.
  const pulseSelectionGeometryBlocked = useCallback(() => {
    internalShapeManagerRef.current?.triggerSelectionGeometryBlockedPulse()
  }, [internalShapeManagerRef])

  return {
    lockSelectedObjects,
    unlockSelectedObjects,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    toggleFreezeSelectedObjects,
    unlockAllObjects,
    unfreezeAllObjects,
    pulseSelectionGeometryBlocked,
  }
}
